import type { AxiosPromise } from "axios";
import { route } from "../../shim/jsbridge";

import { AxiosApiWrapper } from "../../services/api/AxiosApiWrapper";
import type {
  AnalysisStatus,
  DnsReport,
  DomainProp,
  DomainReport,
  DomainResolutionsResponse,
  DomainUrlsResponse,
  IPDomainResolutionsResponse,
  IPDownloadedFiles,
  IPReport,
  IPUrlsResponse,
  PayloadsReport,
  QueryParamProps,
  RelatedDomainsResponse,
  UrlReport,
  WhoIsReport
} from "./@types";
import { BgpReport } from "./@types/IP";
import { NetworkReputationReport } from "./@types/Url";

type PaginationRequest = {
  page?: string;
  pageSize?: string | number;
};

type DownloadedFilesRequest = {
  extended?: boolean;
} & PaginationRequest;

class Api extends AxiosApiWrapper {
  urlReputation = (data: unknown): AxiosPromise<NetworkReputationReport> =>
    this.post(route("api-network-reputation"), data);
  urlReport = ({ url }: { url: string }): AxiosPromise<UrlReport> =>
    this.get(route("api-network-threat-intel-url", {}, { url }));

  domainReport = ({ domain }: { domain: string }): AxiosPromise<DomainReport> =>
    this.get(route("api-network-threat-intel-domain", [domain]));

  ipReport = ({ ip }: { ip: string }): AxiosPromise<IPReport> =>
    this.get(route("api-network-threat-intel-ip-report", { value: ip }));

  dnsReport = ({ url }: { url: string }): AxiosPromise<DnsReport> =>
    this.post(route("api-network-threat-intel-dns"), {
      url
    });

  whoisReport = ({ url }: { url: string }): AxiosPromise<WhoIsReport> =>
    this.post(route("api-network-threat-intel-whois-tool"), {
      uri: url
    });

  bgpReport = ({ ip }: { ip: string }): AxiosPromise<BgpReport> =>
    this.get(route("api-network-threat-intel-bgp-tool", { ip }));

  getUrlPayloads = ({
    url,
    queryParams
  }: {
    url: string;
    queryParams: QueryParamProps;
  }): AxiosPromise<PayloadsReport> =>
    this.post(route("api-sample-browse-url-payloads", [url], queryParams), {
      url
    });

  ipResolutions = ({
    ip,
    page,
    pageSize
  }: {
    ip: string;
    page?: string;
    pageSize?: number;
  }): AxiosPromise<IPDomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-resolutions",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainResolutions = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<DomainResolutionsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-resolutions",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainRelatedDomains = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<RelatedDomainsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-related-domains",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainUrls = ({
    domain,
    page,
    pageSize
  }: DomainProp & PaginationRequest): AxiosPromise<DomainUrlsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-domain-urls",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  ipURLs = ({
    ip,
    page,
    pageSize
  }: {
    ip: string;
    page?: string;
    pageSize?: number;
  }): AxiosPromise<IPUrlsResponse> =>
    this.get(
      route(
        "api-network-threat-intel-ip-urls",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize })
        }
      )
    );

  domainDownloadedFiles = ({
    domain,
    page,
    pageSize,
    extended
  }: DomainProp & DownloadedFilesRequest) =>
    this.get(
      route(
        "api-network-threat-intel-domain-downloaded-files",
        { value: domain },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended })
        }
      )
    );

  urlDownloadedFiles = ({
    url,
    page,
    pageSize,
    extended
  }: {
    url: string;
  } & DownloadedFilesRequest) =>
    this.post(route("api-network-threat-intel-url-downloaded-files"), {
      url,
      ...(page && { page }),
      ...(pageSize && { page_size: pageSize }),
      ...(extended && { extended })
    });

  ipDownloadedFiles = ({
    ip,
    page,
    pageSize,
    extended
  }: {
    ip: string;
  } & DownloadedFilesRequest): AxiosPromise<IPDownloadedFiles> =>
    this.get(
      route(
        "api-network-threat-intel-ip-downloaded-files",
        { value: ip },
        {
          ...(page && { page }),
          ...(pageSize && { page_size: pageSize }),
          ...(extended && { extended })
        }
      )
    );

  urlDynamicAnalysisStatus = (
    url: string,
    signal: AbortSignal
  ): AxiosPromise<AnalysisStatus> =>
    this.post(
      route("api_rl_dynamic_analysis_url_report_display_status"),
      {
        url
      },
      { signal }
    );

  getAnalysisStatus = (url: string, signal: AbortSignal) =>
    this.post<AnalysisStatus>(
      route("url_analysis_status"),
      { url },
      { signal }
    );
}

export default new Api();
